import { titleizeString } from '@chillibean/component-library';

import { formatValue } from './formattingHelpers';

export const generateStaticColumns = (studyProtocol, tasks) => {
  const studyEventDisplayName = titleizeString(studyProtocol?.studyEventDisplayName?.singular || 'Study Event');
  const studyProcedureDisplayName = titleizeString(
    studyProtocol?.studyProcedureDisplayName?.singular || 'Study Procedure',
  );

  return [
    { Header: 'Trial', accessor: 'accountTrialName' },
    { Header: 'Trial Config Name', accessor: 'studyProtocol|name' },
    { Header: 'Site Name', accessor: 'site|name' },
    { Header: 'Site ID', accessor: 'site|id' },
    { Header: 'Subject ID', accessor: 'subject|displayName' },
    { Header: `${studyEventDisplayName} Name`, accessor: 'studyEvent|displayName' },
    { Header: `${studyEventDisplayName} Arm`, accessor: 'studyArm' },
    { Header: `${studyEventDisplayName} Status`, accessor: 'studyEvent|status' },
    { Header: `${studyEventDisplayName} Date`, accessor: 'studyEvent|date' },
    { Header: `${studyProcedureDisplayName} ID`, accessor: 'id' },
    { Header: `${studyProcedureDisplayName} Name`, accessor: 'displayName' },
    { Header: `${studyProcedureDisplayName} Date`, accessor: 'date' },
    { Header: `${studyProcedureDisplayName} Status`, accessor: 'status' },
    { Header: `${studyProcedureDisplayName} Status Date`, accessor: 'statusChangeDate' },
    { Header: 'Clinical Evaluator', accessor: 'clinicalEvaluator' },
    { Header: 'Action Required Flag Raised', accessor: 'flaggedTasksRaised' },
    {
      Header: 'Action Required Flag',
      accessor: 'flaggedTasks|reasonForCreation',
    },
    {
      Header: 'Action Required - Date Raised',
      accessor: 'flaggedTasks|createdAt',
    },
    {
      Header: 'Action Resolved - Reason',
      accessor: 'flaggedTasks|reasonForResolving',
    },
    { Header: 'Action Resolved Date', accessor: 'flaggedTasks|resolvedAt' },
    ...(tasks
      ? tasks
          .map((_, index) => [
            { Header: `Task ${index + 1}`, accessor: `task|${index}|definition` },
            {
              Header: `Task ${index + 1} Status`,
              accessor: `task|${index}|status`,
            },
            {
              Header: `Task ${index + 1} Completed Date`,
              accessor: `task|${index}|completedDate`,
            },
            {
              Header: `Task ${index + 1} Completed by`,
              accessor: `task|${index}|completedBy`,
            },
          ])
          .flat()
      : []),
    { Header: 'Completed Forms', accessor: 'completedForms' },
    { Header: 'Assets', accessor: 'assets' },
  ];
};

export const prepareColumnHeaders = (data) => {
  const headers = [];
  const columns = generateStaticColumns(data?.[0]?.studyProtocol, data?.[0]?.tasks);

  columns.forEach((col) => {
    headers.push({
      ...col,
      accessor: col.accessor,
      formatting: col.formatting !== undefined ? col.formatting : true,
    });
  });

  return headers;
};

export const prepareAssetRowData = (data) => {
  const headersReadyForTable = prepareColumnHeaders(data);
  const dataReadyForTable = [];
  data.forEach((assetEntry) => {
    const preparedRow = {};
    headersReadyForTable.forEach((header) => {
      const nestedAccessor = header.accessor.split('|');
      let retrievedValue;

      if (nestedAccessor.length === 2) {
        if (nestedAccessor[0] === 'flaggedTasks') {
          const flaggedTasks = assetEntry?.flaggedTasks;
          retrievedValue = flaggedTasks.map((task) => task?.[nestedAccessor[1]]);
        } else {
          retrievedValue = assetEntry?.[nestedAccessor[0]]?.[nestedAccessor[1]];
        }
      } else if (nestedAccessor.length === 3) {
        // tasks
        const taskIndex = nestedAccessor[1];
        retrievedValue = assetEntry?.tasks[taskIndex]?.[nestedAccessor[2]];

        if (nestedAccessor[2] === 'status') {
          retrievedValue = assetEntry?.tasks[taskIndex]?.completedDate ? 'Complete' : 'Not Complete';
        }
      } else {
        retrievedValue = assetEntry?.[header.accessor];

        if (header.accessor === 'completedForms') {
          retrievedValue = assetEntry?.[header.accessor].map((form) => form.id);
        }
      }

      preparedRow[header.accessor] = formatValue({
        rawValue: retrievedValue,
        assetEntry: assetEntry,
        formattingEnabled: header.formatting,
        accessor: header.accessor,
      });
    });
    dataReadyForTable.push(preparedRow);
  });

  return { dataReadyForTable, headersReadyForTable };
};

export default prepareAssetRowData;
